import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { RootState } from "@/store";

// The MapLibre camera at the onboarding entry. The center, the zoom, the bearing
// and the pitch are on the map instance and not in Redux. Thus the app parks them
// here and puts them back at the exit. The value is null if no map is mounted.
export type MapCamera = {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
};

/**
 * A tour that runs, and the data that only a tour parks. The shared
 * `analysisConfigSnapshot` keeps the other data. A tour parks `ui`, because it
 * opens panels for the user. It also parks the map camera and the route for the
 * exit. The route is null in an old session, and then the exit does not navigate.
 */
export type OnboardingSession = {
  moduleCode: string;
  parkedUi: unknown;
  parkedMapCamera: MapCamera | null;
  entryPath: string | null;
};

export interface OnboardingState {
  // The tourId of the step on the screen. It is not in the local state of the
  // tour, thus components out of the tour can use it. For example, a sidebar nav
  // item stays in its hover state while its step is on the screen. The value is
  // null if no step is active.
  activeStepTourId: string | null;
  // Null when no tour is running.
  session: OnboardingSession | null;
}

export const onboardingInitialState: OnboardingState = {
  activeStepTourId: null,
  session: null
};

/**
 * The app persists `session`. Thus after a page refresh the tour knows the module
 * and the data for the exit. `activeStepTourId` is temporary UI data, and
 * `store/index.ts` keeps it out of the persisted data.
 *
 * The shared analysis slices of the tour are in `state.analysisConfigSnapshot`.
 * That slice also controls the workspace ownership between this mode and
 * Agentic AI.
 */
export const onboardingSlice = createSlice({
  name: "onboarding",
  initialState: onboardingInitialState,
  reducers: {
    setActiveStepTourId: (state, action: PayloadAction<string | null>) => {
      state.activeStepTourId = action.payload;
    },
    /**
     * This reducer has no guard. `parkAnalysisConfig` claims the workspace, and a
     * caller comes here only after it reads that claim back.
     */
    enterOnboarding: (state, action: PayloadAction<OnboardingSession>) => {
      state.session = action.payload;
    },
    exitOnboarding: (state) => {
      state.session = null;
    },
    resetOnboardingState: () => onboardingInitialState
  }
});

export const {
  setActiveStepTourId,
  enterOnboarding,
  exitOnboarding,
  resetOnboardingState
} = onboardingSlice.actions;

export const onboardingReducer = onboardingSlice.reducer;

export const selectOnboardingSession = (
  state: RootState
): OnboardingSession | null => state.onboarding.session;

export const selectIsOnboardingActive = (state: RootState): boolean =>
  state.onboarding.session !== null;

export const selectOnboardingModuleCode = (state: RootState): string | null =>
  state.onboarding.session?.moduleCode ?? null;
